import React, { useEffect, useState, useCallback } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Bell, BellOff, BellRing } from 'lucide-react';
import {
  isSupported, getPermission, isMuted, setMuted, requestPermission, onPermissionChange, notify,
} from '../utils/notificationService';
import { ensurePushSubscription, disablePushSubscription } from '../utils/pushSubscription';

/**
 * Navbar control for system (mobile/desktop) notifications:
 *   • permission "default"  → click asks the browser for permission
 *   • permission "granted"  → click toggles mute on/off
 *   • permission "denied"   → disabled, tooltip explains how to re-enable
 */
const NotificationBell = () => {
  const [, force] = useState(0);
  const rerender = useCallback(() => force((n) => n + 1), []);

  useEffect(() => onPermissionChange(rerender), [rerender]);

  // If notifications were already granted (and not muted) on a previous visit,
  // (re-)register this device for web push on load so a fresh deploy / new
  // device / rotated VAPID key keeps real OS push working without re-asking.
  useEffect(() => {
    if (getPermission() === 'granted' && !isMuted()) ensurePushSubscription();
  }, []);

  if (!isSupported()) return null;

  const permission = getPermission();
  const muted = isMuted();

  const handleClick = async () => {
    if (permission === 'default') {
      const res = await requestPermission();
      if (res === 'granted') {
        const pushed = await ensurePushSubscription();
        notify({
          message: pushed
            ? 'Notifications enabled — you’ll be alerted on this device even when the app is closed.'
            : 'Notifications enabled — you’ll be alerted while this tab is in the background.',
          severity: 'success',
        });
      }
      return;
    }
    if (permission === 'granted') {
      const next = !muted;
      setMuted(next);
      // Muting fully unsubscribes the device from server push; unmuting re-subscribes.
      if (next) disablePushSubscription();
      else ensurePushSubscription();
      notify({ message: next ? 'Notifications muted' : 'Notifications unmuted', severity: 'info' });
    }
  };

  let title;
  let Icon;
  let color = 'text.secondary';
  if (permission === 'denied') {
    title = 'Notifications blocked — enable them in your browser site settings';
    Icon = BellOff;
  } else if (permission === 'default') {
    title = 'Enable notifications';
    Icon = Bell;
  } else if (muted) {
    title = 'Notifications muted — click to unmute';
    Icon = BellOff;
  } else {
    title = 'Notifications on — click to mute';
    Icon = BellRing;
    color = 'primary.main';
  }

  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          onClick={handleClick}
          disabled={permission === 'denied'}
          sx={{ color }}
          aria-label="Notifications"
        >
          <Icon size={20} />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default NotificationBell;
